import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/modern/components/ui/avatar';
import { Button } from '@/modern/components/ui/button';
import { Phone, Mail, Calendar, Clock, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export interface ResourceDetail {
  name: string;
  description: string;
  phone: string;
  email: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  duration: string;
}

interface ResourceDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resources: ResourceDetail[];
}

const extractInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

export const ResourceDetailModal: React.FC<ResourceDetailModalProps> = ({
  open,
  onOpenChange,
  resources
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[--swivu-bryntum-z-index] max-w-[400px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 pt-6 pb-2 sticky top-0 bg-background z-10 border-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-normal">
              Scheduled Resources ({resources.length})
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-muted"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-6 space-y-4">
          {resources.map((resource, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 bg-card"
            >
              <div className="flex items-start gap-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                    {extractInitials(resource.name)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground break-words">
                    {resource.name}
                  </h3>
                  <p className="text-sm text-muted-foreground break-words">
                    {resource.description}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">{resource.phone}</span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground truncate">{resource.email}</span>
                </div>

                <div className="border-t border-border pt-2 mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-foreground">
                      {format(parseISO(resource.startDate), 'EEE M-d')} {resource.startTime} → {format(parseISO(resource.endDate), 'EEE M-d')} {resource.endTime}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground">{resource.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {resources.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No scheduled resources found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDetailModal;
